import { create } from "zustand";
import { ConversationTask, ConversationTaskType } from "@/types";

interface TaskManagerStore {
  activeTask: Partial<ConversationTask> | null;
  setActiveTask: (value: Partial<ConversationTask> | null) => void;
  selectedTaskIndex: string | null;
  setSelectedTaskIndex: (value: string | null) => void;
  taskManagerOption: ConversationTaskType | null;
  setTaskManagerOption: (value: ConversationTaskType | null) => void;
  selectedTasksIds: string[];
  setSelectedTasksIds: (value: string[]) => void;
  setAddTask: (value: boolean) => void;
  addTask: boolean;
  initializeHashSync: () => void; // Initialize hash synchronization
}

export const useTaskManagerStore = create<TaskManagerStore>((set, get) => {
  // Parse the task type from the URL hash
  const parseHash = (): ConversationTaskType | null => {
    const hash = window.location.hash.slice(1); // Remove the `#`
    if (hash.startsWith("modal-task-manager")) {
      const [, taskType] = hash.split(":");
      if (
        taskType === ConversationTaskType.CUSTOM ||
        taskType === ConversationTaskType.SUMMARY_PUSH ||
        taskType === ConversationTaskType.FOLLOW_UP_EMAIL ||
        taskType === ConversationTaskType.PDF_FILL ||
        taskType === ConversationTaskType.EXCEL_FILL
      ) {
        return taskType as ConversationTaskType;
      }
    }
    return null;
  };

  // Update the URL hash based on the task type
  const updateHash = (taskType: ConversationTaskType | null) => {
    if (taskType) {
      window.location.hash = `modal-task-manager:${taskType}`;
    } else {
      if (window.location.hash.startsWith("#modal-task-manager")) {
        // Use replaceState to avoid conflicts with Next.js navigation
        window.history.replaceState(
          null,
          "",
          window.location.pathname + window.location.search,
        );
      }
    }
  };

  return {
    activeTask: null,
    setActiveTask: (value) => set({ activeTask: value }),
    selectedTaskIndex: null,
    setSelectedTaskIndex: (value) => set({ selectedTaskIndex: value }),
    taskManagerOption: null,
    setTaskManagerOption: (value) => {
      set({ taskManagerOption: value });
      if (value) {
        // Update hash to reflect the task type
        window.location.hash = `modal-task-manager:${value}`;
      } else {
        // Clear hash if the modal is closed
        // Use replaceState instead of pushState to avoid conflicts with Next.js navigation
        if (window.location.hash.startsWith("#modal-task-manager")) {
          window.history.replaceState(
            null,
            "",
            window.location.pathname + window.location.search,
          );
        }
      }
    },

    selectedTasksIds: [],
    setSelectedTasksIds: (value) => set({ selectedTasksIds: value }),
    addTask: false,
    setAddTask: (value) => set({ addTask: value }),

    // Initialize hash synchronization
    initializeHashSync: () => {
      const handleHashChange = () => {
        const taskType = parseHash();
        set({ taskManagerOption: taskType });
      };

      // Sync initial hash state
      handleHashChange();

      // Add hash change listener
      window.addEventListener("hashchange", handleHashChange);

      return () => {
        window.removeEventListener("hashchange", handleHashChange);
      };
    },
  };
});
